<script>
	import { Editor, EditorContent } from '@tiptap/vue-2'
	import StarterKit from '@tiptap/starter-kit'
	import Placeholder from '@tiptap/extension-placeholder'
	import ListItem from '@tiptap/extension-list-item';
	import { mapGetters, mapState } from "vuex";

	let _uid = 0;

	export default {
		name: "BaseStylableTextarea",
		components: {
			EditorContent
		},
		props: {
			placeholder: { type: String, required: false, default: "" },
			value: { type: String, required: false, default: "" },
			autofocus: { type: Boolean, required: false, default: false },
			tooltip: { type: Object, required: false, default: null },
			nullable: { type: Boolean, required: false, default: false },
			privateId: { type: String, required: false, default: null }
		},
		data() {
			return {
				editor: null,
				showToolbar: false,
				toolbarStyle: { top: '0px', left: '0px' },
				internalId: "bs_" + (++_uid)
			};
		},
		computed: {
			idBase() {
				if (this.tooltip) return this.tooltip.target;
				return this.internalId;
			},
			...mapGetters(["computedPrintableLang"]),
			...mapState({
				activeBaseStylableTextareaId: state => state.home.activeBaseStylableTextareaId
			}),
			isActive() {
				return this.activeBaseStylableTextareaId === this.idBase;
			},
			valueLength() {
				return this.value ? this.value.replace(/<p>\s*(<br[^>]*>)?\s*<\/p>/g, '').trim().length : 0;
			},
			inactiveValue() {
				return this.value ? this.addPragraphToBr(this.addBrToEmptyParagraphs(this.value)) : '';
			}
		},
		watch: {
			isActive(newValue) {
				if (newValue) {
					this.$nextTick(() => {
						this.initEditor();
						this.$nextTick(() => {
							if (this.editor) {
								this.editor.commands.focus();
							}
						});
					});
				}
				else if (this.editor) {
					this.destroyEditor();
				}
			},
			value(newVal) {
				// Force la value à '' si null ou undefined
				const safeVal = (newVal == null) ? '' : newVal;
				const normalizedValue = this.sanitizeEditorHtml(safeVal);
				const currentEditorHtml = this.editor ? this.editor.getHTML() : '';
				if (this.editor && normalizedValue !== currentEditorHtml) {
					this.editor.commands.setContent(normalizedValue, false); // false to avoid infinite loop with editor event as we watch value
				}
			}
		},
		mounted() {},
		beforeDestroy() {
			this.destroyEditor();
		},
		methods: {
			destroyEditor() {
				if (!this.editor) return;
				const editor = this.editor;
				this.editor = null;

				// Defer destroy to a macrotask so it runs AFTER Vue's microtask re-render,
				// which lets EditorContent.beforeDestroy access editor.view.dom safely.
				setTimeout(() => {
					try {
						editor.destroy();
					} catch (_) {
						// L'editeur peut deja etre en cours de destruction.
					}
				}, 0);
			},
			sanitizeEditorHtml(html) {
				if (!html) return html;

				return html
					.replace(/<br\s+[^>]*class=["'][^"']*ProseMirror-trailingBreak[^"']*["'][^>]*\/?>(?:<\/br>)?/gi, '')
					.replace(/<br\s+[^>]*class=["'][^"']*ProseMirror[^"']*["'][^>]*\/?>(?:<\/br>)?/gi, '<br>')
					.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '<p></p>');
			},
			addBrToEmptyParagraphs(text) {
				return text ? text.replace(/<p>\s*<\/p>/gi, '<p><br></p>') : '';
			},
			addPragraphToBr(text) {
				return text ? text.split('\n').map(line => line.replace(/^<br>$/i, '<p><br></p>')).join('\n') : '';
			},
			updateToolbar(editor) {
				const { from, to } = editor.state.selection;
				if (from === to) {
					this.showToolbar = false;
					return;
				}
				// Get selection coordinates
				const view = editor.view;
				const start = view.coordsAtPos(from);
				const end = view.coordsAtPos(to);
				const containerRect = this.$el.getBoundingClientRect();

				const left = (start.left + end.left) / 2 - containerRect.left;
				const top = start.top - containerRect.top - 55;

				this.toolbarStyle = {
					left: left + 'px',
					top: top + 'px'
				};
				this.showToolbar = true;
			},
			focus() {
				this.editor?.commands.focus();
			},
			initEditor() {
				//destroy existing editor if any
				this.destroyEditor();
				// Force la value à '' si null ou undefined
				const safeVal = (this.value == null) ? '' : this.sanitizeEditorHtml(this.value);
				// replace <p><br></p> by <p></p> to avoid empty paragraphs with a <br> that create extra space at the end of the content
				// create new editor
				this.editor = new Editor({
					content: safeVal,
					editorProps: {
						handleDOMEvents: {
							keydown: (_, event) => {
								if (event.key === 'Enter') {
									event.stopPropagation();
								}
								return false;
							}
						}
					},
					extensions: [
						StarterKit,
						Placeholder.configure({
							placeholder: this.placeholder
						}),
						ListItem
					],
					onFocus: () => {
						this.$emit('focus');
					},
					onBlur: () => {
						const editor = this.editor;
						if (!editor) return;

						let safeVal = '';
						try {
							safeVal = editor.getHTML() == null ? '' : editor.getHTML();
						} catch (_) {
							return;
						}

						if (!this.nullable && safeVal === '<p></p>') {
							this.destroyEditor();
							this.$emit('input', '');
							if (this.activeBaseStylableTextareaId === this.idBase) {
								this.$store.commit('activeBaseStylableTextareaId', null);
							}
						}
						// delay to allow button clicks in toolbar
						setTimeout(() => {
							if (!this.$el.contains(document.activeElement)) {
								this.showToolbar = false;
								this.$emit('blur');
								//this.$store.commit('activeBaseStylableTextareaId', null);
							}
						}, 200);
					},
					onSelectionUpdate: ({ editor }) => {
						this.updateToolbar(editor);
					},
					onTransaction: ({ editor }) => {
						this.updateToolbar(editor);
						// Émet la nouvelle valeur à chaque changement
						const html = editor.getHTML();
						//if (html !== this.value) {
							this.$emit('input', html);
						//}
					},
					//onDestroy: () => {
					//	this.editor = null;
					//}
				});
			},
		}
	};
</script>

<template lang="html">
	<div>
		<div v-if="isActive" :id="idBase" class="stylable-textarea">
			<div
				v-if="editor && showToolbar"
				class="bubble-menu"
				:style="toolbarStyle"
			>
				<button
					:class="{ 'is-active': editor.isActive('bold') }"
					@mousedown.prevent="editor.chain().focus().toggleBold().run()"
					title="Gras"
				>
					<b-icon-type-bold font-scale="2" />
				</button>
				<button
					:class="{ 'is-active': editor.isActive('italic') }"
					@mousedown.prevent="editor.chain().focus().toggleItalic().run()"
					title="Italique"
				>
					<b-icon-type-italic font-scale="2" />
				</button>
				<!--button
					:class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
					@mousedown.prevent="editor.chain().focus().toggleHeading({ level: 1 }).run()"
					title="Titre 1"
				>
					H1
				</button>
				<button
					:class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
					@mousedown.prevent="editor.chain().focus().toggleHeading({ level: 2 }).run()"
					title="Titre 2"
				>
					H2
				</button>
				<button
					:class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
					@mousedown.prevent="editor.chain().focus().toggleHeading({ level: 3 }).run()"
					title="Titre 3"
				>
					H3
				</button -->
				<button
					:class="{ 'is-active': editor.isActive('bulletList') }"
					@mousedown.prevent="editor.chain().focus().toggleBulletList().run()"
					title="Liste à puces"
				>
					<b-icon-list-ul font-scale="2" />
				</button>
				<button
					:class="{ 'is-active': editor.isActive('orderedList') }"
					@mousedown.prevent="editor.chain().focus().toggleOrderedList().run()"
					title="Liste numérotée"
				>
					<b-icon-list-ol font-scale="2" />
				</button>
			</div>
			<EditorContent
				:editor="editor"
			/>
		</div>
		<div
			v-else
			:id="String(idBase)+'-inactive'"
			@click="$store.commit('activeBaseStylableTextareaId', idBase)"
			class="stylable-textarea"
		>
			<div>
				<template v-if="valueLength">
					<div v-html="inactiveValue"></div>
				</template>
				<span v-else class="placeholderText">{{ placeholder }}</span>
			</div>

		</div>
		<b-tooltip
			v-if="tooltip"
			placement="left"
			:target="tooltip.target"
			container="body"
		>
			{{ tooltip.text }}
		</b-tooltip>
	</div>
</template>

<style lang="scss" scoped>
	@import "../../scss/variables.scss";

	li {
		text-align: justify;
	}
	p {
		margin-bottom: 0 !important;
	}
	::v-deep dl,
	::v-deep ol,
	::v-deep ul {
		margin-bottom: 0 !important;
	}
	::v-deep .ProseMirror dl,
	::v-deep .ProseMirror ol,
	::v-deep .ProseMirror ul,
	::v-deep dl,
	::v-deep ol,
	::v-deep ul {
		margin: 0 !important;
	}
	.placeholderText {
		color: rgba(0, 0, 0, 0.6);
		white-space: nowrap;      /* Empêche le retour à la ligne */
		overflow: hidden;         /* Cache le contenu qui dépasse */
		text-overflow: ellipsis;  /* Ajoute "..." à la fin */
		max-width: 100%;          /* Largeur maximale = 100% du parent */
		display: block;           /* Assure que max-width fonctionne correctement */
	}

	.bubble-menu {
		position: absolute;
		z-index: 100;
		transform: translateX(-50%);
		display: flex;
		gap: 4px;
		background: #444;
		border-radius: 50px;
		padding: 6px 12px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);

		&::after {
			content: "";
			position: absolute;
			bottom: -6px;
			left: 50%;
			margin-left: -6px;
			border-left: 6px solid transparent;
			border-right: 6px solid transparent;
			border-top: 6px solid #444;
		}

		button {
			background: none;
			border: none;
			cursor: pointer;
			display: flex;
			justify-content: center;
			align-items: center;
			height: 24px;
			padding: 3px 5px;
			width: 24px;
			color: #ccc;
			border-radius: 4px;
			line-height: 1;
			transition: background 0.15s, color 0.15s;

			&:hover {
				color: #fff;
			}

			&.is-active {
				background: rgba(255, 255, 255, 0.1);
				color: #fff;
			}

			.b-icon {
				width: 22px;
				height: 22px;
				padding: 0 !important;
			}
		}
	}

	.stylable-textarea {
		position: relative;
		hyphens: auto;
		white-space: normal;
		text-justify: inter-word;
		cursor: text;
		&.billing__input {
			padding: 0;
			hyphens: auto;
			word-wrap: normal;
			white-space: normal;
		}

		// Justify text in inactive (read-only) mode
		&:not(.ProseMirror) {
			text-align: justify;
		}

		   ::v-deep .ProseMirror {
			   min-height: 0;
			   /* padding: 8px 16px 8px 8px; */
			   font-size: $font-size-normal;
			   height: auto;
			   font-family: Roboto, sans-serif;
			   outline: none !important;
			   box-shadow: none !important;
			   border: none !important;
		   }


		   ::v-deep .ProseMirror:focus,
		   ::v-deep .ProseMirror:focus-visible,
		   ::v-deep .ProseMirror.ProseMirror-focused {
			   outline: none !important;
			   box-shadow: none !important;
			   border: none !important;
		   }

		   .ProseMirror > * + * {
			   margin-top: 0.25em;
		   }

		   .ProseMirror p.is-editor-empty:first-child::before {
			   content: attr(data-placeholder);
			   float: left;
			   color: rgba(0, 0, 0, 0.4);
			   font-style: italic;
			   pointer-events: none;
			   height: 0;
		   }

}

::v-deep p, ::v-deep p.is-empty.is-editor-empty {
	margin: 0 !important;
}

</style>
